import { Link } from "react-router-dom";
import { ArrowUpRight, Truck, Leaf, ShieldCheck, Timer } from "lucide-react";

const MEAT_IMG =
  "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMHByZW1pdW0lMjBjdXQlMjBtZWF0fGVufDB8fHx8MTc4MzA5MTQxMnww&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
  { name: "Chicken", note: "Antibiotic-free · pasture raised", price: "from ₹199" },
  { name: "Mutton", note: "Signature Deccan flock", price: "from ₹649" },
  { name: "Goat", note: "Farm-fresh, hand-cut", price: "from ₹599" },
  { name: "Cold Cuts", note: "Chef-cured, small-batch", price: "from ₹279" },
];

const FEATURES = [
  { icon: Truck, k: "45-min delivery", v: "Cold-chain routing in 2 pilot cities." },
  { icon: Leaf, k: "Farm-verified", v: "Every SKU tied to a named partner farm." },
  { icon: ShieldCheck, k: "FSSAI compliant", v: "Slaughter and processing audited monthly." },
  { icon: Timer, k: "6-hour freshness", v: "From cut to doorstep in under 6 hours." },
];

export default function MeatApp() {
  return (
    <div data-testid="freshcut-page" className="bg-[#F2ECE2]">
      {/* Hero */}
      <section className="border-b border-terracotta/20">
        <div className="container-x py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="overline text-terracotta">/ Product · LiveCuts</div>
            <h1
              data-testid="freshcut-heading"
              className="mt-6 font-display font-black text-[clamp(3rem,7vw,6.5rem)] leading-[0.9] tracking-tighter text-terracotta"
            >
              LiveCuts.
            </h1>
            <div className="mt-2 font-serif italic text-2xl sm:text-3xl text-ink-olive">
              Fresh meat, delivered from farm to kitchen.
            </div>
            <p className="mt-8 max-w-2xl text-lg text-ink-soft leading-relaxed">
              LiveCuts is our in-house fresh-meat delivery app for chef-grade
              cuts. Cold-chain routing across Hyderabad, transparent sourcing
              from partner farms and small-batch curing — built end-to-end by
              the Thiruhanarayano product team.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="inline-flex items-center gap-2 bg-terracotta text-bone px-6 py-3 font-sans font-medium hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(140,58,58,0.35)] transition-all duration-300"
                data-testid="freshcut-download-android"
              >
                Google Play <ArrowUpRight size={18} />
              </a>
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="inline-flex items-center gap-2 border border-terracotta text-terracotta px-6 py-3 font-sans font-medium hover:bg-terracotta hover:text-bone transition-colors"
                data-testid="freshcut-download-ios"
              >
                App Store <ArrowUpRight size={18} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden border border-terracotta/40">
              <img
                src={MEAT_IMG}
                alt="Chef-grade cuts"
                className="w-full h-full object-cover"
                data-testid="freshcut-hero-image"
              />
              <div className="absolute top-4 left-4 mono-label bg-terracotta text-bone px-2 py-1">
                / v2.0 · Live in Hyderabad
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-terracotta/20 bg-white">
        <div className="container-x py-24 sm:py-32">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.k}
                  className="border border-terracotta/20 p-8 bg-white"
                  data-testid={`freshcut-feature-${i}`}
                >
                  <span className="inline-flex w-11 h-11 items-center justify-center bg-terracotta/10 text-terracotta">
                    <Icon size={20} />
                  </span>
                  <div className="mt-6 font-display font-bold text-xl text-ink-olive">
                    {f.k}
                  </div>
                  <p className="mt-2 text-ink-soft text-sm leading-relaxed">
                    {f.v}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="border-b border-terracotta/20">
        <div className="container-x py-24 sm:py-32">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="overline text-terracotta">/ Menu</div>
              <h2 className="mt-4 font-serif italic font-light text-4xl sm:text-5xl text-terracotta">
                Cuts that respect the animal.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-terracotta/20 border border-terracotta/20">
            {CATEGORIES.map((c, i) => (
              <div
                key={c.name}
                className="bg-[#F2ECE2] p-8 hover:bg-white transition-colors"
                data-testid={`freshcut-category-${i}`}
              >
                <div className="mono-label text-terracotta">/ 0{i + 1}</div>
                <div className="mt-4 font-display font-bold text-2xl text-ink-olive">
                  {c.name}
                </div>
                <div className="mt-2 text-ink-soft text-sm">{c.note}</div>
                <div className="mt-6 font-mono text-sm text-terracotta">
                  {c.price}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-terracotta text-bone">
        <div className="container-x py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <h3 className="lg:col-span-8 font-display font-black text-4xl sm:text-5xl tracking-tighter">
            Retail partner or investor?
          </h3>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link
              to="/contact"
              data-testid="freshcut-cta-partner"
              className="inline-flex items-center gap-2 bg-bone text-terracotta px-6 py-3 font-sans font-medium hover:bg-ink-olive hover:text-bone transition-colors"
            >
              Partner with us <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
